import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { IMAGES, OFFERS } from "@/data/site";
import { Reveal, MaskedLine, ChapterTitle } from "@/components/Reveal";
import { EventCard, specialeFirst } from "@/components/EventCard";
import { SuperLuneEncart } from "@/components/SuperLuneEncart";
import { BookingDialog } from "@/components/BookingDialog";
import { ContactForm } from "@/components/ContactForm";
import { SocialLinks } from "@/components/SocialLinks";
import { LieuBooking } from "@/components/LieuBooking";
import { ConsultationBooking } from "@/components/ConsultationBooking";
import { ConsultationsApproches, ConsultationsTarifs } from "@/components/ConsultationsSections";
import { StageFormules } from "@/components/StageFormules";
import { StageReassurance } from "@/components/StageReassurance";
import { track } from "@/lib/analytics";
import { api } from "@/lib/api";
import { useSeo, breadcrumb, absolute } from "@/lib/seo";
import { SoinsSections, SoinRequestForm, BtnNeSaisPas } from "@/components/SoinsSections";
import { LieuxAnciensFormules, LieuxAnciensComparatif, LieuxAnciensInclus, LieuxAnciensMeteo } from "@/components/LieuxAnciensSections";
import { MateriauxOrigine } from "@/components/MateriauxOrigine";
import { SoireesDecouverte } from "@/components/SoireesDecouverte";
import { RandonneeTerrain } from "@/components/RandonneeTerrain";
import { SuiviFormuleAction, SuiviFormuleNote } from "@/components/LieuSuiviFormules";
import { LieuSuiviRequestForm } from "@/components/LieuSuiviRequestForm";
import { LA_PRIVEE_MESSAGES, LA_FORMULES } from "@/data/lieuxAnciens";
import { CerclesPaiensSections } from "@/components/CerclesPaiensSections";
import { isCerclesPaiensLive } from "@/lib/cerclesPaiens";
import { useLocale } from "@/context/LocaleContext";
import { STAGE_PRICING } from "@/data/stagePricing";

const SLUG_EXTRAS = {
  "stage-fabrication-tambour": { eventTypes: ["stage"], formSujet: null },
  "tambours-lieux-anciens": { eventTypes: ["lieu_ancien"], formSujet: "Création sur un lieu ancien", lieuxAnciens: true },
  "cercles-evenements": { eventTypes: ["cercle", "formation"], formSujet: null },
  "soins-energetiques": { eventTypes: null, formSujet: "Soin énergétique", soins: true },
  "lieux-habitats": { eventTypes: null, formSujet: "Harmonisation de lieu", lieuBooking: true },
  "consultations-holistiques": { eventTypes: null, formSujet: "Consultation holistique", consultationBooking: true },
};

const OfferPage = () => {
  const { slug } = useParams();
  const offer = OFFERS[slug];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const [events, setEvents] = useState([]);
  const [bookingEvent, setBookingEvent] = useState(null);
  const [soinFormule, setSoinFormule] = useState(null);
  const [priveeFormule, setPriveeFormule] = useState("individuel");
  const [consultMode, setConsultMode] = useState(null);
  const [suiviFormule, setSuiviFormule] = useState(null);
  const extras = SLUG_EXTRAS[slug] || {};
  const isStage = slug === "stage-fabrication-tambour";
  const isCercles = slug === "cercles-evenements";
  const { locale } = useLocale();
  const en = locale === "en";
  const circlesLive = isCerclesPaiensLive();
  useEffect(() => { if (isStage) track("view_drum_stage_page"); }, [isStage]);
  const onBook = (e) => { if (isStage) track("start_drum_booking", { event: e.titre, date: e.date.slice(0, 10) }); setBookingEvent(e); };
  const choisirPrivee = (id) => { setPriveeFormule(id); document.getElementById("privee")?.scrollIntoView({ behavior: "smooth" }); };
  const choisirConsult = (id) => { setConsultMode(id); document.getElementById("consultation")?.scrollIntoView({ behavior: "smooth" }); };
  const choisirSuivi = (id) => { setSuiviFormule(id); document.getElementById("demande-suivi")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  useSeo({
    title: isCercles ? (en ? "Pagan Circles in French-speaking Switzerland | Entre Les Mondes" : "Cercles Païens en Suisse romande | Entre Les Mondes") : offer?.seoTitle,
    description: isCercles ? (en ? "Discover Entre Les Mondes Pagan Circles: living traditions, ritual celebrations, drums and reconnection with nature’s cycles." : "Découvrez les Cercles Païens d’Entre Les Mondes : traditions vivantes, célébrations rituelles, tambours et reconnexion aux cycles de la nature.") : offer ? offer.seoDescription || `${offer.sousTitre} ${offer.accroche}` : "",
    image: offer ? IMAGES[offer.heroImage] : undefined,
    jsonLd: offer ? [
      breadcrumb([["Accueil", "/"], [offer.titre, `/offres/${slug}`]]),
      { "@type": "Service", name: offer.titre, description: offer.sousTitre, serviceType: offer.titre, provider: { "@id": "https://entrelesmondes.net/#organisation" }, areaServed: ["Suisse romande", "Vaud", "Genève", "Neuchâtel", "Fribourg", "Valais"], url: absolute(`/offres/${slug}`), image: IMAGES[offer.heroImage],
        ...(extras.consultationBooking ? { offers: [["Question ciblée — 15 min à distance", 20], ["Consultation holistique — 45 min à distance", 50], ["Consultation holistique — 60 min en présentiel à Orbe", 100]].map(([name, price]) => ({ "@type": "Offer", name, price, priceCurrency: "CHF", availability: "https://schema.org/InStock", url: absolute(`/offres/${slug}#tarifs-consultation`) })) } : {}) },
    ] : null,
  });

  useEffect(() => {
    if (extras.eventTypes) {
      api.events()
        .then((all) => setEvents(specialeFirst(all.filter((e) => extras.eventTypes.includes(e.type)))))
        .catch(() => {});
    }
  }, [slug]);

  if (!offer) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="font-serif text-3xl text-bark mb-6">Cette page s'est évaporée comme la fumée de sauge.</p>
        <Link to="/" data-testid="offer-notfound-home" className="link-line text-terra">Retour à l'accueil</Link>
      </div>
    );
  }

  if (isCercles) {
    const heroTitle = circlesLive ? (en ? "Pagan Circles" : "Les Cercles Païens") : (en ? "Pagan Circles, from 1 November 2026" : "Les Cercles Païens, dès le 1er novembre 2026");
    const heroSubtitle = en ? "Living rituals · Traditions · Drums" : "Rituels vivants · Traditions · Tambours";
    return <div data-testid="cercles-paiens-page">
      <section ref={heroRef} className="relative bg-earth grain overflow-hidden flex items-end min-h-[78vh]" data-testid="cercles-paiens-hero">
        <motion.div style={{ y }} className="absolute inset-0"><img src={IMAGES.cercle} alt="" className="w-full h-[115%] object-cover opacity-45" /><div className="absolute inset-0 bg-gradient-to-t from-earth via-earth/55 to-earth/20" /></motion.div>
        <div className="relative z-[2] w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-44"><Link to="/" data-testid="cercles-paiens-back-home" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/60 hover:text-sand transition-colors mb-8"><ArrowLeft className="w-3.5 h-3.5" /> Entre les Mondes</Link><p className="eyebrow text-sand/70 mb-5" data-testid="cercles-paiens-kicker">{circlesLive ? (en ? "Public ritual gatherings" : "Rencontres rituelles publiques") : (en ? "Official evolution announced" : "Évolution officielle annoncée")}</p><h1 className="font-serif text-sand text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl" data-testid="cercles-paiens-title"><MaskedLine delay={0.2}>{heroTitle}</MaskedLine></h1><motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.75 }} className="mt-6 max-w-2xl text-sand/75 leading-relaxed" data-testid="cercles-paiens-subtitle">{heroSubtitle}</motion.p><motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }} className="mt-8 flex flex-wrap gap-4"><a href="#celebrations" data-testid="cercles-paiens-next-cta" className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-terra text-earth text-sm hover:bg-sand hover:text-earth transition-colors duration-300">{en ? "Discover upcoming celebrations" : "Découvrir les prochaines célébrations"} <ArrowRight className="w-4 h-4" /></a><Link to="/agenda" data-testid="cercles-paiens-schedule-cta" className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-sand/35 text-sand text-sm hover:border-terra hover:text-terra transition-colors duration-300">{en ? "View the schedule" : "Consulter l’agenda"} <ArrowRight className="w-4 h-4" /></Link></motion.div></div>
      </section>
      <CerclesPaiensSections events={events} onBook={setBookingEvent} />
      <BookingDialog event={bookingEvent} open={!!bookingEvent} onOpenChange={(value) => !value && setBookingEvent(null)} />
    </div>;
  }

  return (
    <div>
      {/* Hero */}
      <section ref={heroRef} className="relative bg-earth grain overflow-hidden flex items-end min-h-[78vh]" data-testid="offer-hero">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={IMAGES[offer.heroImage]} alt="" className="w-full h-[115%] object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-earth via-earth/50 to-earth/30" />
        </motion.div>
        <div className="relative z-[2] w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-44">
          <Link to="/" data-testid="offer-back-home" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/60 hover:text-sand transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> Entre les Mondes
          </Link>
          <h1 className="font-serif text-sand text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            <MaskedLine delay={0.25}>{offer.titre}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-6 max-w-2xl text-sand/75 leading-relaxed"
          >
            {offer.sousTitre}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }}>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a href={extras.soins ? "#quel-soin" : extras.lieuxAnciens ? "#dates-lieux-anciens" : isStage ? "#dates" : isCercles ? "#dates-groupe" : "#offer-action"} onClick={() => isStage && track("click_group_dates", { source: "hero" })} data-testid="offer-hero-cta"
              className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-sand hover:text-earth transition-colors duration-500">
              {offer.cta} <ArrowRight className="w-4 h-4" />
            </a>
            {extras.soins && <BtnNeSaisPas light testid="soins-ne-sais-pas-hero" />}
            {isStage && (
              <a href="#individuel" onClick={() => track("click_individual_booking", { source: "hero" })} data-testid="stage-hero-individuel" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full border border-sand/40 text-sand text-sm tracking-wide hover:bg-sand hover:text-earth transition-colors duration-300">
                {offer.ctaSecondaire} <ArrowRight className="w-4 h-4" />
              </a>
            )}
            {extras.lieuxAnciens && (
              <a href="#privee" data-testid="la-hero-privee" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full border border-sand/40 text-sand text-sm tracking-wide hover:bg-sand hover:text-earth transition-colors duration-300">
                {offer.ctaSecondaire} <ArrowRight className="w-4 h-4" />
              </a>
            )}
            <span className="inline-flex items-center px-5 py-3.5 rounded-full border border-sand/30 text-sand/80 font-mono text-xs tracking-[0.2em]">
              Offre {offer.rang} / {String(Object.keys(OFFERS).length).padStart(2, "0")}
            </span>
          </motion.div>
        </div>
      </section>

      {isCercles && <section id="dates-groupe" className="lg:hidden bg-sand-dark/50 border-y border-sand-dark scroll-mt-20" data-testid="cercles-mobile-dates">
        <div className="px-6 py-12">
          <p className="eyebrow mb-4">Cercles & pratique du tambour</p>
          <h2 className="font-serif text-3xl text-bark leading-tight">Les prochaines rencontres</h2>
          <p className="mt-3 text-sm text-fumee leading-relaxed">Choisissez un cercle ou une formation autour du tambour. Retrouvez toutes les autres activités dans l’agenda.</p>
          <div className="mt-7 space-y-4">
            {events.length > 0 ? events.map((event, index) => <EventCard key={event.id} event={event} index={index} onBook={onBook} />) : <p className="text-sm text-fumee">Chargement des prochaines dates…</p>}
          </div>
          <Link to="/agenda?type=cercles" data-testid="cercles-mobile-agenda-link" className="inline-flex items-center gap-2 mt-8 text-sm text-terra link-line">Voir tout l’agenda <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>}

      {extras.soins && <SoinsSections onChoisir={(f) => { setSoinFormule(f); document.getElementById("offer-action")?.scrollIntoView({ behavior: "smooth" }); }} />}

      {/* Bienfaits */}
      {!extras.soins && <section className={`max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 ${isCercles ? "hidden lg:block" : ""}`}>
        <Reveal>
          <ChapterTitle numero="Chapitre I" titre="Ce que vous y trouverez" />
          <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight max-w-2xl mb-14">{offer.accroche}</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-6">
          {offer.bienfaits.map((b, i) => (
            <Reveal key={b.titre} delay={i * 0.08}>
              <div className="h-full bg-card border border-sand-dark rounded-2xl p-8 hover:-translate-y-1 hover:shadow-lg hover:shadow-earth/5 transition-all duration-500" data-testid={`benefit-${i}`}>
                <span className="font-mono text-xs text-terra tracking-[0.3em]">0{i + 1}</span>
                <h3 className="font-serif text-2xl text-bark mt-4 mb-3">{b.titre}</h3>
                <p className="text-sm text-fumee leading-relaxed">{b.texte}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>}

      {extras.consultationBooking && <><ConsultationsApproches /><ConsultationsTarifs onChoisir={choisirConsult} /></>}
      {isStage && <StageFormules />}
      {isStage && <StageReassurance />}
      {extras.lieuxAnciens && <LieuxAnciensFormules onPrivee={choisirPrivee} />}
      {(slug === "stage-fabrication-tambour" || extras.lieuxAnciens) && <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 lg:pb-32 space-y-6"><SuperLuneEncart /><MateriauxOrigine testid={extras.lieuxAnciens ? "materiaux-origine-la" : "materiaux-origine-stage"} /></section>}

      {/* Processus */}
      {!extras.soins && <section className={`bg-earth grain text-sand relative ${isCercles ? "hidden lg:block" : ""}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 relative z-[2]">
          <Reveal>
            <p className="eyebrow mb-6">Le déroulement</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight max-w-xl mb-16">Pas à pas, en toute clarté.</h2>
          </Reveal>
          <div className={`grid gap-10 ${offer.processus.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
            {offer.processus.map((step, i) => (
              <Reveal key={step.titre} delay={i * 0.1}>
                <div className="border-t border-sand/20 pt-6" data-testid={`process-step-${i}`}>
                  {step.image && (
                    <div className="rounded-2xl overflow-hidden aspect-square mb-6 group">
                      <img src={step.image} alt={step.alt || step.titre} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" data-testid={`process-step-image-${i}`} />
                    </div>
                  )}
                  <span className="font-serif text-5xl text-terra/80">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-xl mt-4 mb-3">{step.titre}</h3>
                  <p className="text-sm text-sand/65 leading-relaxed">{step.texte}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>}

      {extras.lieuxAnciens && <><LieuxAnciensComparatif /><LieuxAnciensInclus /><LieuxAnciensMeteo /></>}

      {/* Situations (lieux & habitats, consultations) */}
      {offer.situations && (
        <section className="bg-earth grain text-sand relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center relative z-[2]" data-testid="offer-situations">
            <Reveal className="lg:col-span-7">
              <p className="eyebrow mb-6 text-sand/70">{offer.situationsTitre || "Dans quelles situations faire appel à moi ?"}</p>
              <ul className="space-y-4">
                {offer.situations.map((s, i) => (
                  <li key={s} className="flex gap-5 items-baseline border-b border-sand/15 pb-4">
                    <span className="font-serif text-2xl text-terra">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sand/85">{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sand/60 text-sm leading-relaxed max-w-xl">{offer.situationsNote || "Chaque situation est différente. Mon travail commence toujours par une lecture globale du lieu avant de déterminer ce qui nécessite réellement une intervention."}</p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden aspect-square"><img src={IMAGES[offer.situationsImage || "lieuxDetail"]} alt="" loading="lazy" className="w-full h-full object-cover" /></div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Infos pratiques + témoignage */}
      {!extras.soins && <section id={extras.lieuBooking ? "tarifs" : undefined} className={`max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 scroll-mt-28 ${isCercles ? "hidden lg:grid" : ""}`}>
        <Reveal className="lg:col-span-7">
          <ChapterTitle numero={extras.lieuxAnciens ? "Chapitre IV" : "Chapitre II"} titre={extras.lieuBooking ? "Tarifs & suivi" : extras.consultationBooking ? "Formats & tarifs" : "Informations pratiques"} />
          <dl className="divide-y divide-sand-dark border-y border-sand-dark">
            {offer.pratique.map((p) => (
              <div key={p.label} className="py-5 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-8" data-testid={`pratique-${p.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <dt className="font-mono text-xs uppercase tracking-[0.2em] text-fumee sm:w-56 shrink-0">{p.label}</dt>
                <dd className="text-bark">{p.valeur}{extras.lieuBooking && <SuiviFormuleAction label={p.label} onChoisir={choisirSuivi} />}</dd>
              </div>
            ))}
          </dl>
          {extras.lieuBooking && <SuiviFormuleNote />}
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="bg-sage-light/40 border border-sage/25 rounded-2xl p-8 lg:p-10 h-full flex flex-col justify-center">
            <p className="eyebrow mb-6">Avis Google · 5/5</p>
            <blockquote className="font-serif text-xl lg:text-2xl text-bark leading-relaxed italic">« {offer.temoignage.texte} »</blockquote>
            <footer className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-fumee">{offer.temoignage.auteur}</footer>
          </div>
        </Reveal>
      </section>}

      {/* Action : événements ou formulaire */}
      <section id="offer-action" className={`bg-sand-dark/50 border-y border-sand-dark ${isCercles ? "hidden lg:block" : ""}`} data-testid="offer-action">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          {extras.eventTypes ? (
            <>
              <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
                <div id={extras.lieuxAnciens ? "dates-lieux-anciens" : isStage ? "dates" : "dates-groupe"} className="scroll-mt-28">
                  <p className="eyebrow mb-6">{extras.lieuxAnciens ? "Journées collectives signatures 2027 — CHF 600.– / pers." : isStage ? `Stage en groupe — dès CHF ${STAGE_PRICING.groupe}.– · tambour rond · 8 personnes max.` : "Prochaines dates en groupe — tambour rond"}</p>
                  <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">{isStage ? "Choisissez votre prochaine date" : offer.cta}</h2>
                  {isStage && <p className="text-fumee mt-4 max-w-xl leading-relaxed">Date, horaire, lieu, tarif et places restantes : cliquez sur « Réserver » pour confirmer votre place et payer en ligne (TWINT / carte) ou sur facture. Acompte de CHF 150.– possible.</p>}
                  {extras.lieuxAnciens && <p className="text-fumee mt-4 max-w-xl leading-relaxed" data-testid="la-dates-note">Journée complète, 8 personnes maximum. Le lieu précis est communiqué aux participant·e·s avant chaque date. Acompte de CHF 150.– possible, solde au plus tard le jour de la journée.</p>}
                </div>
                {!isStage && <Link to="/agenda" data-testid="offer-agenda-link" className="link-line text-sm text-fumee hover:text-bark inline-flex items-center gap-2">
                  Agenda complet <ArrowRight className="w-4 h-4" />
                </Link>}
              </Reveal>
              {slug === "cercles-evenements" && (
                <Reveal className="mb-14">
                  <div className="bg-card border border-sand-dark rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="cercles-social">
                    <div>
                      <p className="font-serif text-2xl text-bark">Rejoignez le cercle, entre deux rendez-vous.</p>
                      <p className="text-sm text-fumee mt-2">Le canal WhatsApp annonce les dates ; la communauté permet d'échanger entre participant·e·s.</p>
                    </div>
                    <SocialLinks dark={false} withLabels />
                  </div>
                </Reveal>
              )}
              {events.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.map((e, i) => <EventCard key={e.id} event={e} index={i} onBook={onBook} />)}
                </div>
              ) : (
                <p className="text-fumee">Chargement des dates…</p>
              )}
              {isStage && (
                <Reveal className="mt-12 border border-sand-dark bg-card rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="stage-aucune-date">
                  <div>
                    <p className="font-serif text-2xl text-bark">Aucune date ne vous convient ?</p>
                    <p className="text-sm text-fumee mt-1.5">Choisissez votre propre journée : création individuelle CHF {STAGE_PRICING.individuel}.–, à la date libre de votre choix.</p>
                  </div>
                  <a href="#individuel" onClick={() => track("click_individual_booking", { source: "aucune-date" })} data-testid="stage-aucune-date-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-earth text-sand text-sm shrink-0 hover:bg-terra hover:text-earth transition-colors duration-300">Choisir une journée en individuel <ArrowRight className="w-4 h-4" /></a>
                </Reveal>
              )}
              {extras.lieuxAnciens && (
                <div id="privee" className="mt-24 scroll-mt-28 grid lg:grid-cols-12 gap-12 items-start" data-testid="la-privee">
                  <Reveal className="lg:col-span-5">
                    <p className="eyebrow mb-6">Création privée — sur demande, de mai à septembre</p>
                    <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight mb-6">Organiser une création privée <em className="not-italic text-terra">sur un haut lieu.</em></h2>
                    <p className="text-fumee leading-relaxed mb-8">Seul·e (CHF 750.–), à deux (CHF 650.– / pers.) ou en privatisation pour 3 à 4 personnes (sur demande) : indiquez la formule, la période et la région qui vous attirent. Ludovic vous répond sous 48 h avec une proposition de lieu et de date.</p>
                    <div className="flex flex-wrap gap-2" role="group" aria-label="Formule privée">
                      {LA_FORMULES.filter((f) => f.id !== "collectif").map((f) => (
                        <button key={f.id} type="button" onClick={() => setPriveeFormule(f.id)} data-testid={`la-privee-formule-${f.id}`}
                          className={`px-4 py-2 rounded-full text-sm transition-colors duration-300 ${priveeFormule === f.id ? "bg-earth text-sand" : "border border-wood/25 text-fumee hover:border-terra hover:text-terra"}`}>{f.titre}</button>
                      ))}
                    </div>
                  </Reveal>
                  <Reveal delay={0.15} className="lg:col-span-7">
                    <div className="bg-card border border-sand-dark rounded-2xl p-8 lg:p-10">
                      <ContactForm key={priveeFormule} sujetPreset={extras.formSujet} messagePreset={LA_PRIVEE_MESSAGES[priveeFormule]} titre="Organiser une création privée" testidPrefix="la-privee" />
                    </div>
                  </Reveal>
                </div>
              )}
            </>
          ) : extras.lieuBooking ? (
            <>
              <div id="visite" className="mb-20 scroll-mt-28">
                <Reveal className="mb-10">
                  <p className="eyebrow mb-6">Réserver une visite — selon la taille du lieu, dès CHF 150.– · grands lieux sur devis</p>
                  <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight max-w-2xl">
                    Choisissez le jour et l'heure <em className="not-italic text-terra">de la visite de Ludovic.</em>
                  </h2>
                  <p className="text-fumee mt-4 max-w-xl leading-relaxed">
                    Une visite d'environ deux heures chez vous : lecture du lieu, diagnostic, harmonisation et conseils. Acompte de CHF 150.– possible, solde au plus tard le jour de la visite.
                  </p>
                </Reveal>
                <LieuBooking />
              </div>
              <div className="grid lg:grid-cols-12 gap-12 items-start">
                <Reveal className="lg:col-span-5">
                  <p className="eyebrow mb-6">Une question, un devis ?</p>
                  <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight mb-6">Parlons d'abord de votre lieu.</h2>
                  <p className="text-fumee leading-relaxed mb-8">
                    Espace professionnel, situation complexe, suivi annuel ou simple doute : décrivez ce qui vous amène et Ludovic vous répond personnellement sous 48 h.
                  </p>
                  <p className="text-sm text-fumee">
                    Consultez aussi la <Link to="/faq" data-testid="offer-faq-link" className="link-line text-terra">FAQ</Link>.
                  </p>
                </Reveal>
                <Reveal delay={0.15} className="lg:col-span-7">
                  <div className="bg-card border border-sand-dark rounded-2xl p-8 lg:p-10">
                    <LieuSuiviRequestForm formuleInitiale={suiviFormule} />
                  </div>
                </Reveal>
              </div>
            </>
          ) : extras.consultationBooking ? (
            <>
              <div id="consultation" className="mb-20 scroll-mt-28">
                <Reveal className="mb-10">
                  <p className="eyebrow mb-6">Réserver — CHF 20.– question ciblée 15 min · CHF 50.– consultation 45 min à distance · CHF 100.– consultation 60 min à Orbe</p>
                  <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight max-w-2xl">
                    Choisissez le format, le jour et l'heure <em className="not-italic text-terra">qui vous conviennent.</em>
                  </h2>
                  <p className="text-fumee mt-4 max-w-xl leading-relaxed">
                    Question ciblée de 15 min ou consultation de 45 min par visio, téléphone ou WhatsApp ; consultation de 60 min à l'atelier d'Orbe. Le créneau est bloqué pour vous dès la réservation.
                  </p>
                </Reveal>
                <ConsultationBooking modeInitial={consultMode} />
              </div>
              <Reveal>
                <div className="bg-card border border-sand-dark rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="consultation-contact">
                  <div>
                    <p className="font-serif text-2xl text-bark">Vous hésitez encore, ou votre question ne rentre dans aucune case ?</p>
                    <p className="text-sm text-fumee mt-2">Écrivez à Ludovic : il vous répond personnellement sous 48 h, en toute simplicité.</p>
                  </div>
                  <Link to="/contact" data-testid="offer-contact-link" className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-wood/25 text-sm text-bark hover:border-terra hover:text-terra transition-colors duration-300 shrink-0">
                    Poser une question <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            </>
          ) : (
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              <Reveal className="lg:col-span-5">
                <p className="eyebrow mb-6">Premier pas</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight mb-6">{offer.cta}</h2>
                <p className="text-fumee leading-relaxed mb-8">
                  {extras.soins ? "Le formulaire reprend la formule que vous avez choisie ci-dessus (par défaut : soin énergétique & réalignement, CHF 50.–). Transmettez votre intention ; le soin est réalisé dans les 48 heures et vous recevez un retour par WhatsApp." : "Parlez-nous de ce qui vous amène : nous vous proposons un premier échange en toute simplicité, et le soin est réalisé à distance sous 48 h."}
                </p>
                {extras.soins && <BtnNeSaisPas className="mb-8" testid="soins-ne-sais-pas-form" />}
                <p className="text-sm text-fumee">
                  Vous préférez d'abord une question ?{" "}
                  <Link to="/contact" data-testid="offer-contact-link" className="link-line text-terra">Contactez-nous</Link> ou consultez la{" "}
                  <Link to="/faq" data-testid="offer-faq-link" className="link-line text-terra">FAQ</Link>.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="lg:col-span-7">
                <div className="bg-card border border-sand-dark rounded-2xl p-8 lg:p-10">
                  {extras.soins ? <SoinRequestForm formule={soinFormule} onReset={() => setSoinFormule(null)} /> : <ContactForm sujetPreset={extras.formSujet} titre={offer.cta} testidPrefix={`offer-${slug}`} />}
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      {extras.lieuBooking && <RandonneeTerrain />}
      {extras.lieuBooking && <SoireesDecouverte />}

      {/* Maillage interne */}
      <section className={`max-w-7xl mx-auto px-6 lg:px-10 py-20 ${isCercles ? "hidden lg:block" : ""}`}>
        <Reveal className="flex flex-wrap items-center justify-between gap-6">
          <p className="font-serif text-2xl text-bark">Explorer une autre voie</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(OFFERS).filter(([s]) => s !== slug).map(([s, o]) => (
              <Link key={s} to={`/offres/${s}`} data-testid={`offer-link-${s}`}
                className="px-5 py-2.5 rounded-full border border-wood/25 text-sm text-fumee hover:border-terra hover:text-terra transition-colors duration-300">
                {o.titreCourt}
              </Link>
            ))}
            <Link to="/boutique" data-testid="offer-link-boutique"
              className="px-5 py-2.5 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors duration-300">
              La boutique
            </Link>
          </div>
        </Reveal>
      </section>

      <BookingDialog event={bookingEvent} open={!!bookingEvent} onOpenChange={(v) => !v && setBookingEvent(null)} />
    </div>
  );
};

export default OfferPage;
